"use client";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NextImage from "@/app/components/lib/NextImage";
import { canHover } from "@/utils/functions/canHover";
import useIsRtl from "@/hooks/useIsRtl";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { nestedScrollerProps } from "@/app/components/lib/Embla-slider/nestedScroller";

/** Product cards never show more than this many images. */
export const MAX_CARD_SLIDES = 5;

/**
 * Pixels a mouse must travel before a press counts as a pan rather than a click. Below
 * it the card still navigates, so a slightly unsteady hand never swallows a click.
 */
const DRAG_THRESHOLD_PX = 5;

/**
 * How long the track must be still before a scroll counts as settled.
 *
 * `scrollend` is the event this wants, but it is Safari 18+ and our floor is 15.4 — so
 * idleness is inferred by debouncing `scroll` instead. The handler only touches timers,
 * never layout, so it adds no forced reflow to the scroll frames.
 */
const SCROLL_IDLE_MS = 120;

/**
 * Moves the scroller without animating, whatever `scroll-behavior` says.
 *
 * Done by toggling the CSS property rather than passing ScrollToOptions'
 * `behavior: "instant"`: that is a WebIDL enum, so an engine whose enum predates the
 * value throws a TypeError instead of ignoring it.
 */
const jumpTo = (track: HTMLDivElement, left: number) => {
  const previous = track.style.scrollBehavior;
  track.style.scrollBehavior = "auto";
  track.scrollLeft = left;
  track.style.scrollBehavior = previous;
};

interface Props {
  /**
   * Resolved, de-duplicated image URLs in the product's own order — `[0]` is the
   * default image and the LCP candidate. Videos are filtered out upstream: a card
   * grid cannot afford a <video> element per box.
   */
  slides: string[];
  alt: string;
  priority: boolean;
  objectFit: "contain" | "cover" | "fill";
  sizes: string;
  /**
   * CSS length for the aspect box, applied as `padding-top` (e.g. `max(143px,100%)`).
   * It is present from the very first paint, which is what makes mounting the later
   * slides on demand incapable of shifting layout.
   */
  aspectPadding?: string;
  imgWidth?: number;
  imgHeight?: number;
}

/** Right-pointing chevron. SCSS mirrors it via `--pb-dir` for RTL and for `prev`. */
const Chevron = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M9 5l7 7-7 7"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The product-card image slider.
 *
 * Swiping is native CSS scroll-snap, so it runs entirely on the compositor: a grid of
 * 40 cards adds ZERO main-thread input handling. The alternative — reusing the PDP's
 * `ThumbMailSlider` — would have put an Embla instance (pointer handlers, ResizeObserver,
 * init measurement) on every card and pulled `embla-carousel` into the listing bundle,
 * which has no Embla today. See the listener-leak note in `useEmblaSliderFeatures`.
 *
 * Loading policy: every snap target is rendered up-front as an EMPTY div, so the
 * geometry is final at first paint, but only the default image carries an <img>.
 * The rest mount on interaction (desktop hover, or the press that starts a pan) and
 * one slide ahead of wherever the shopper settles. A card nobody touches therefore
 * costs exactly the one image it costs today.
 */
const ProductBoxImageSlider: React.FC<Props> = ({
  slides,
  alt,
  priority,
  objectFit,
  sizes,
  aspectPadding,
  imgWidth,
  imgHeight,
}) => {
  const isRtl = useIsRtl();
  const t = useTranslation();
  const count = slides.length;

  // What actually goes in the DOM: the real slides plus ONE trailing clone of the
  // default image, which is what makes the loop possible.
  //
  // Appending is the only layout-safe place for it. A leading clone would have to be
  // scrolled past on mount to reveal the real first slide, so the card's very first
  // paint would show the LAST product image until an effect corrected it — unacceptable
  // when slide 0 is the LCP candidate. Appending cannot move the current scroll offset,
  // so `scrollLeft` starts at 0 on the real default image exactly as before.
  //
  // The clone reuses slide 0's URL, so it is the same next/image src and costs no extra
  // request — only a second element.
  const domSlides = useMemo(
    () => (count > 1 ? [...slides, slides[0]] : slides),
    [slides, count],
  );
  const domCount = domSlides.length;

  // One integer instead of a Set: bit N means "slide N has a real <img>". Bit 0 starts
  // set (the default image always renders). Because the state is a primitive, a repeat
  // hover or a scroll that mounts nothing new bails out of re-rendering entirely —
  // a new Set() would have re-rendered every time.
  const [loadedMask, setLoadedMask] = useState(1);
  const [index, setIndex] = useState(0);
  // Index tracking and the observer are installed on first interaction only, so a card
  // the shopper never touches runs no slider JS at all.
  const [active, setActive] = useState(false);

  const trackRef = useRef<HTMLDivElement>(null);
  const indexRef = useRef(0);
  // The DOM index, which unlike `index` can also be the trailing clone. Read by the
  // idle handler, which must know whether the track is parked on the clone.
  const domIndexRef = useRef(0);
  const idleTimerRef = useRef(0);
  // Synced in an effect, not during render — mutating a ref while rendering is not allowed.
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const mountAround = useCallback(
    (i: number) => {
      setLoadedMask((mask) => {
        let next = mask;
        for (const k of [i - 1, i, i + 1]) {
          if (k >= 0 && k < domCount) next |= 1 << k;
        }
        return next;
      });
    },
    [domCount],
  );

  const activateAll = useCallback(() => {
    setActive(true);
    setLoadedMask((1 << domCount) - 1);
  }, [domCount]);

  // Desktop: a hovering pointer is the strongest "this card is being inspected" signal
  // there is, and the arrows are one click away — so fetch the whole set.
  const handleMouseEnter = useCallback(() => {
    // Touch devices DO fire mouseover on tap; the cached media query keeps this off the
    // mobile INP path entirely (see canHover()).
    if (!canHover()) return;
    activateAll();
  }, [activateAll]);

  // Mouse drag-to-pan state. `null` whenever no drag is in progress, which is always
  // the case on touch — see handlePointerDown.
  const dragRef = useRef<{
    startX: number;
    startScroll: number;
    moved: boolean;
  } | null>(null);
  // Survives the drag so the click that follows it can be swallowed.
  const draggedRef = useRef(false);

  // A press on the track: the pan has not settled yet, so the neighbouring slides are
  // already in flight by the time it does.
  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      setActive(true);
      mountAround(indexRef.current);

      // Drag-to-pan is for the MOUSE only. Touch already pans on the compositor for
      // free; hijacking it with pointermove would move the swipe onto the main thread
      // and wreck INP on exactly the devices this slider was built for. A trackpad
      // needs nothing either — two-finger horizontal scroll drives an overflow
      // container natively. This is only for the mouse user, who otherwise has no
      // gesture at all and has to reach for the arrows.
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      const track = trackRef.current;
      if (!track) return;

      dragRef.current = {
        startX: e.clientX,
        startScroll: track.scrollLeft,
        moved: false,
      };
      // Pointer capture keeps the move/up events coming to this element even when the
      // cursor leaves the card, so a fast drag cannot be dropped half-way.
      track.setPointerCapture(e.pointerId);
    },
    [mountAround],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const track = trackRef.current;
    if (!drag || !track) return;

    const dx = e.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      // Mandatory snap fights an in-progress drag: the browser re-snaps on every
      // scroll change, so intermediate positions spring straight back. `scroll-behavior:
      // smooth` would likewise animate each individual write. The class drops both for
      // the duration of the drag — see _product-box-slider.scss.
      track.classList.add("is-dragging");
    }
    // `scrollLeft` is defined physically in both writing directions — dragging right
    // always reveals what lies to the left — so this one expression covers LTR and the
    // negative RTL range alike.
    track.scrollLeft = drag.startScroll - dx;
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const track = trackRef.current;
    dragRef.current = null;
    if (!drag || !track) return;

    if (track.hasPointerCapture(e.pointerId)) track.releasePointerCapture(e.pointerId);
    if (!drag.moved) return;

    // Swallow the click this drag is about to produce, or releasing the mouse navigates
    // to the product page.
    draggedRef.current = true;
    track.classList.remove("is-dragging");
    // Settle on the nearest slide explicitly rather than trusting the re-snap that
    // restoring `scroll-snap-type` is supposed to trigger. Math.round is symmetric about
    // zero, so this lands correctly on both the positive LTR range and the negative RTL
    // one, and the restored `scroll-behavior: smooth` animates it.
    const width = track.clientWidth;
    if (width > 0) track.scrollTo({ left: Math.round(track.scrollLeft / width) * width });
  }, []);

  const handleClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!draggedRef.current) return;
    draggedRef.current = false;
    // The capture phase reaches the track before the ancestor <Link>: preventDefault
    // cancels the anchor's navigation, stopPropagation keeps ProductClick out of it.
    e.preventDefault();
    e.stopPropagation();
  }, []);

  useEffect(() => {
    if (!active || count < 2) return;
    const track = trackRef.current;
    if (!track) return;

    // An IntersectionObserver rooted on the track — deliberately NOT a scroll listener.
    // It reads no layout during the scroll (so no per-frame forced reflow), fires only
    // when the settled slide actually changes, and needs no RTL sign arithmetic:
    // `scrollLeft` is negative in an RTL container and positive in an LTR one, but
    // visibility is direction-agnostic.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const i = Number((entry.target as HTMLElement).dataset.slide);
          domIndexRef.current = i;
          // The clone shows the default image, so it IS logical slide 0 as far as the
          // dots are concerned. Taken from the observer rather than from scrollLeft, so
          // it needs no RTL sign arithmetic.
          setIndex(i % count);
          mountAround(i);
        }
      },
      // Slides are exactly one track wide, so only the settled one can clear 60%.
      { root: track, threshold: 0.6 },
    );
    for (const slide of Array.from(track.children)) observer.observe(slide);
    return () => observer.disconnect();
  }, [active, count, domCount, mountAround]);

  // A configurable product swaps its default image when the shopper picks a variant.
  // Rewind the scroller, or the card stays parked on slide 3 of the outgoing variant.
  //
  // Deliberately the ONLY thing this does. `index` re-derives itself from the observer
  // once the scroll lands, and the mounted mask is left alone: every slide URL has just
  // changed, so nothing stale can be on screen, and a shopper who is already picking
  // variants is better served by the new variant's images being ready than by paying
  // the fetch again on the next hover. Slides are keyed positionally for the same
  // reason — the <img> elements survive the swap, so the outgoing bitmap stays painted
  // until the incoming one decodes instead of flashing the empty card surface.
  const primarySrc = slides[0];
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    // `scroll-behavior: smooth` lives on the track for the arrows, so a rewind has to opt
    // out or it animates sideways through the outgoing variant.
    jumpTo(track, 0);
  }, [primarySrc]);

  // Once the scroll settles on the trailing clone, silently rewind to the real slide 0.
  // The two are pixel-identical, so there is nothing to see — and `scrollLeft = 0` is the
  // first slide in BOTH writing directions, so this needs no RTL special case. From the
  // shopper's side the track simply never runs out of images.
  const normaliseLoop = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    // Never yank the track out from under an in-progress mouse drag.
    if (dragRef.current) return;
    if (domIndexRef.current !== count) return;
    domIndexRef.current = 0;
    jumpTo(track, 0);
  }, [count]);

  const handleScroll = useCallback(() => {
    window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(normaliseLoop, SCROLL_IDLE_MS);
  }, [normaliseLoop]);

  useEffect(() => () => window.clearTimeout(idleTimerRef.current), []);

  const page = useCallback(
    (forward: boolean) => {
      const track = trackRef.current;
      if (!track) return;
      // Desktop-only control. Every engine at our floor (iOS/Safari 15.4+) implements
      // the spec'd RTL model, where scrollLeft is 0 on the first slide and DECREASES
      // toward the last — so "forward" is a negative delta once the track is dir="rtl".
      const step = track.clientWidth * (isRtl ? -1 : 1);
      track.scrollBy({ left: forward ? step : -step });
    },
    [isRtl],
  );

  // Backward from the default image has nowhere to scroll — 0 is the hard edge of the
  // range. So hop to the clone first, which shows that very same image and is therefore
  // invisible, then animate ONE step back off it onto the real last slide. The forward
  // loop's trick, run in reverse.
  const wrapToEnd = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const width = track.clientWidth;
    if (width <= 0) return;
    const sign = isRtl ? -1 : 1;
    jumpTo(track, sign * count * width);
    domIndexRef.current = count;
    track.scrollBy({ left: -sign * width });
  }, [isRtl, count]);

  // The whole card is a <Link>, so both handlers must preventDefault (cancels the
  // anchor) and stopPropagation (keeps ProductClick out of it) before doing anything.
  //
  // They also activate: the arrows are revealed by CSS `:hover`, which needs no JS
  // state, so they are clickable in the one case hover never fired for — the pointer was
  // already inside the card when a variant switch changed the slides.
  const handleNextClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      activateAll();
      // Nothing special at the end: the step past the last real slide lands on the
      // clone, and the idle handler normalises it back to 0 without anything showing.
      page(true);
    },
    [activateAll, page],
  );

  const handlePrevClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      activateAll();
      if (indexRef.current === 0) wrapToEnd();
      else page(false);
    },
    [activateAll, page, wrapToEnd],
  );

  // Single image (also every cart / order-details call site, which passes no `images`):
  // render exactly what shipped before, with no track and no slider markup at all.
  if (count < 2) {
    return (
      <NextImage
        height={imgHeight}
        width={imgWidth}
        src={primarySrc}
        alt={alt}
        priority={priority}
        objectFit={objectFit}
        sizes={sizes}
        containerStyle={{ paddingTop: imgHeight ? 0 : aspectPadding }}
      />
    );
  }

  return (
    <div
      className="pb-slider"
      // Direction is scoped to this image sequence. ProductImg keeps its own dir="ltr"
      // so the badge and wishlist layers stay physically pinned in both languages,
      // which is a deliberate product decision — but the SEQUENCE must mirror: in
      // Arabic the default image sits at the inline start (right) and "next" moves left.
      dir={isRtl ? "rtl" : "ltr"}
      style={imgHeight ? { width: imgWidth, height: imgHeight } : { paddingTop: aspectPadding }}
      onMouseEnter={handleMouseEnter}
    >
      <div
        ref={trackRef}
        className="pb-slider__track"
        // Product cards also live inside Embla carousels. This tells the carousel to
        // decline any gesture that starts here, or the swipe drags the rail instead of
        // paging the images — see Embla-slider/nestedScroller.
        {...nestedScrollerProps}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onScroll={handleScroll}
        onClickCapture={handleClickCapture}
        // The track sits inside an <a> wrapping an <img>: without this a mouse drag
        // starts the browser's native link/image drag instead of panning.
        onDragStart={(e) => e.preventDefault()}
      >
        {domSlides.map((src, i) => (
          // The snap target exists before its image does. That is what keeps the
          // geometry final and makes on-demand mounting incapable of shifting anything.
          <div key={i} className="pb-slider__slide" data-slide={i}>
            {(loadedMask & (1 << i)) !== 0 && (
              <NextImage
                src={src}
                // Additional views carry nothing a screen reader can act on — the card
                // already exposes the product name as a link — so they stay decorative.
                alt={i === 0 ? alt : ""}
                priority={i === 0 && priority}
                // Mounted on demand means the shopper is already looking at it; `lazy`
                // would make the browser defer the very slide that was just swiped to.
                loading={i === 0 ? undefined : "eager"}
                objectFit={objectFit}
                sizes={sizes}
              />
            )}
          </div>
        ))}
      </div>

      {/* Neither arrow is ever disabled: the track loops, so both directions always have
          somewhere to go. They still only exist when there is more than one image, and
          are still hidden outside `hover-supported`. */}
      <button
        type="button"
        className="pb-slider__nav pb-slider__nav--prev"
        aria-label={t.previous}
        onMouseDown={(e) => e.preventDefault()}
        onClick={handlePrevClick}
      >
        <span className="pb-slider__nav-disc">
          <Chevron />
        </span>
      </button>
      <button
        type="button"
        className="pb-slider__nav pb-slider__nav--next"
        aria-label={t.next}
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleNextClick}
      >
        <span className="pb-slider__nav-disc">
          <Chevron />
        </span>
      </button>

      {/* Indicators, not controls: `aria-hidden` + `pointer-events: none` keep 5 dots
          per card out of the accessibility tree and out of the card's tap target.
          Mobile has no arrows, so this is the only cue that more images exist — it is
          therefore always visible, not hover-gated. */}
      <div className="pb-slider__dots" aria-hidden="true">
        {slides.map((_, i) => (
          <span key={i} className={`pb-slider__dot${i === index ? " is-active" : ""}`} />
        ))}
      </div>
    </div>
  );
};

export default React.memo(ProductBoxImageSlider);
